import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import ModalContainer from '../../ModalContainer/ModelContainer'
import Icon from '../../Icons'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import NoteItem from '../components/NoteItem/NoteItem'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

const ReservationNotesModal = ({
  reservationId,
  timezone,
  userData,
  isTimeBlock,
  onClose = () => {},
}) => {
  const { t } = useTranslation(['reservations', 'common'])
  const initialState = {
    title: '',
    noteBody: '',
  }

  const [isLoading, setIsLoading] = useState(true)
  const [showEditor, setShowEditor] = useState(false)
  const [notes, setNotes] = useState([])
  const [formState, setFormState] = useState(initialState)
  const [noteToEdit, setNoteToEdit] = useState(null)
  const { displaySnackbar } = useSnackbar()

  const isFilled = !!formState.title.trim() && !!formState.noteBody.trim()
  const saveNoteEnabled = !noteToEdit
    ? isFilled
    : (formState.title !== noteToEdit.title || formState.noteBody !== noteToEdit.noteBody) &&
      isFilled

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const clearState = () => {
    setIsLoading(false)
    setShowEditor(false)
    setNoteToEdit(null)
    setFormState(initialState)
  }

  const fetchNotes = async () => {
    try {
      setIsLoading(true)
      const result = await API.getReservationNotes(reservationId, isTimeBlock)
      setNotes(result.data)
    } catch ({ message }) {
      console.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveNotes = async () => {
    try {
      setIsLoading(true)
      await API.saveReservationNotes(reservationId, formState, isTimeBlock)
      displaySnackbar(t('reservations:reservationNotesModal.toasts.created'))
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(t('reservations:reservationNotesModal.errors.createFailed'), true)
    } finally {
      clearState()
      fetchNotes()
    }
  }

  const handleUpdateNotes = async () => {
    try {
      setIsLoading(true)
      await API.updateReservationNotes(reservationId, noteToEdit.id, formState, isTimeBlock)
      displaySnackbar(t('reservations:reservationNotesModal.toasts.updated'))
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(t('reservations:reservationNotesModal.errors.updateFailed'), true)
    } finally {
      clearState()
      fetchNotes()
    }
  }

  const onCancel = () => {
    if (showEditor) {
      clearState()
    } else {
      onClose()
    }
  }

  const onDeleteNote = async (noteId) => {
    try {
      await API.deleteReservationNote(reservationId, noteId, isTimeBlock)
    } catch ({ message }) {
      console.error(message)
    } finally {
      fetchNotes()
    }
  }

  useEffect(() => {
    if (reservationId) {
      fetchNotes()
    }
  }, [reservationId])

  useEffect(() => {
    if (noteToEdit) {
      const state = {
        title: noteToEdit.title,
        noteBody: noteToEdit.noteBody,
      }
      setFormState(state)
      setShowEditor(true)
    }
  }, [noteToEdit])

  return (
    <ModalContainer
      headerTitle={t('reservations:reservationNotesModal.headerTitle')}
      onClose={onClose}
      additionalClass="reservations-modal"
      headerIcon={<Icon icon="notebook" viewBox="0 0 18 18" />}
    >
      <div
        className={`reservations-modal-content reservations-notes-modal ${
          isLoading ? 'is-loading' : ''
        }`}
      >
        {showEditor ? (
          <React.Fragment>
            <div className="reservations-modal-input-row">
              <div className="input-container">
                <div className="input-label">{t('reservations:reservationNoteForm.noteName')}</div>
                <TextField
                  name="title"
                  maxLength="200"
                  onChange={handleFieldChange}
                  value={formState.title}
                  placeholder={t('reservations:reservationNoteForm.enterNoteNamePlaceholder')}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className="reservations-modal-input-row">
              <div className="input-container">
                <div className="input-label">
                  {t('reservations:reservationNoteForm.noteDescription')}
                </div>
                <TextAreaField
                  name="noteBody"
                  maxLength="260"
                  onChange={handleFieldChange}
                  value={formState.noteBody}
                  placeholder={t('reservations:reservationNoteForm.notesPlaceholder')}
                  style={{ width: '100%' }}
                />
                <span className="input-desc-label">
                  {t('reservations:reservationNotesModal.maxInputLength')}
                </span>
              </div>
            </div>
          </React.Fragment>
        ) : !notes.length ? (
          <div className="no-notes-container">
            <Icon icon="documentDash" />
            <span>{t('reservations:reservationNotesModal.noNotes')}</span>
            <button
              className="button button-primary auto-width"
              onClick={() => setShowEditor(true)}
            >
              {t('reservations:reservationNotesModal.createNote')}
            </button>
          </div>
        ) : (
          <div className="notes-container">
            {notes.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                onEdit={setNoteToEdit}
                onDelete={onDeleteNote}
                timezone={timezone}
                userData={userData}
              />
            ))}
            <button
              className="button button-primary auto-width"
              onClick={() => setShowEditor(true)}
            >
              {t('reservations:reservationNotesModal.addNewNote')}
            </button>
          </div>
        )}
      </div>
      <div className="reservations-modal-actions">
        <button className="button button-gray" onClick={onCancel} disabled={isLoading}>
          {t('common:actions.cancel')}
        </button>
        <button
          className="button button-primary auto-width"
          onClick={noteToEdit ? handleUpdateNotes : handleSaveNotes}
          disabled={!saveNoteEnabled || isLoading}
        >
          {t('reservations:reservationNotesModal.saveNotes')}
        </button>
      </div>
    </ModalContainer>
  )
}

ReservationNotesModal.propTypes = {
  onClose: PropTypes.func,
  reservationId: PropTypes.number,
  timezone: PropTypes.string,
  userData: PropTypes.object,
  isTimeBlock: PropTypes.bool,
}

export default ReservationNotesModal
